import type { Scene } from '@babylonjs/core/scene';
import type { IVector3 } from '@seddi/math-v2';
import type { Fabric } from '@seddi/scene';
import type { RecolorContext } from '@seddi/ui-recolor';
import log from 'loglevel';
import { FabricAdapter } from './fabricAdapter';
import type { IBabylonFabric } from './fabricTypes';
import type { SurfaceMaterialLoader } from './surfaceMaterialLoader';

/**
 * Loads a fabric.
 */
export class FabricLoader {
  private scene: Scene;
  private surfaceMaterialLoader: SurfaceMaterialLoader;

  constructor(scene: Scene, surfaceMaterialLoader: SurfaceMaterialLoader) {
    this.scene = scene;
    this.surfaceMaterialLoader = surfaceMaterialLoader;
  }

  /**
   * Loads a fabric.
   * @param fabric - Fabric to load.
   */
  async loadRaw(fabric: Fabric): Promise<IBabylonFabric> {
    const sided = FabricAdapter.adapt(fabric);
    const materials = [sided.front];
    if (sided.back) {
      materials.push(sided.back);
    }
    // load materials
    const promises = materials.map(async material =>
      this.surfaceMaterialLoader.loadAsync(material),
    );
    const bmaterials = await Promise.all(promises);

    const fxfabric: IBabylonFabric = { front: bmaterials[0] };
    if (bmaterials.length === 2) {
      fxfabric.back = bmaterials[1];
    }

    return fxfabric;
  }

  /**
   * Loads a fabric with recolor.
   * @param fabric - Fabric to load.
   * @param scene - Fx scene.
   */
  async loadRecolor(
    fabric: Fabric,
    color: IVector3,
    recolorContext: RecolorContext,
  ): Promise<IBabylonFabric> {
    const sided = FabricAdapter.adapt(fabric);
    const materials = [sided.front];
    if (sided.back) {
      materials.push(sided.back);
    }
    // load materials
    const fabricId = fabric.id;
    const promises = materials.map(async (material, i) => {
      const sideId = `${fabricId}-${i}`;
      const baseColorMap = material.params.baseColorMap!;
      await recolorContext.loadEx(sideId, baseColorMap.href, color);
      const recolor = recolorContext.getEx(sideId, color)!;
      baseColorMap.href = recolor.blobUrl;
      const ret = await this.surfaceMaterialLoader.loadAsync(material);
      return ret;
    });
    const bmaterials = await Promise.all(promises);

    const fxFabric: IBabylonFabric = { front: bmaterials[0] };
    if (bmaterials.length === 2) {
      fxFabric.back = bmaterials[1];
    }

    return fxFabric;
  }

  /**
   * Unloads a fabric.
   * @param fabric - Fabric to unload.
   */
  // eslint-disable-next-line @typescript-eslint/require-await -- Todo
  async unloadRaw(fabric: IBabylonFabric) {
    fabric.front.dispose();
    fabric.back?.dispose();
  }

  /**
   * Unloads a fabric.
   * @param fabric - Fabric to unload.
   */
  // eslint-disable-next-line @typescript-eslint/require-await -- Todo
  async unloadRecolor(fabric: IBabylonFabric, _color?: IVector3, _recolorContext?: RecolorContext) {
    // TODO: unload recolor
    fabric.front.dispose();
    fabric.back?.dispose();
  }

  /**
   * Loads the fabric.
   * @param fabric - Fabric to load.
   * @param color - Fabric recolored variant.
   * @returns
   */
  loadAsync(
    fabric: Fabric,
    color?: IVector3,
    recolorContext?: RecolorContext,
  ): Promise<IBabylonFabric> {
    if (color) {
      log.log(`fabric ${fabric.id} with recolor loading`);
      return this.loadRecolor(fabric, color, recolorContext!);
    }
    log.log(`fabric ${fabric.id} loading`);
    return this.loadRaw(fabric);
  }

  /**
   * Unloads a fabric.
   * @param fabric - Fabric to unload.
   * @param scene - Fx scene.
   */
  unload(fabric: IBabylonFabric, color?: IVector3, recolorContext?: RecolorContext): Promise<void> {
    if (color) {
      return this.unloadRecolor(fabric, color, recolorContext);
    }
    return this.unloadRaw(fabric);
  }

  /**
   * Returns a unique fabric ID as a mixture of fabric and color target.
   * @param fabricId - ID of the fabric.
   * @param color - Fabric recolored variant.
   */
  static GetFabricId(fabricId: string, color?: IVector3): string {
    let fabricId_ = fabricId;
    if (color) {
      const r = Number(color.x).toFixed(2);
      const g = Number(color.y).toFixed(2);
      const b = Number(color.z).toFixed(2);
      fabricId_ = `${fabricId}-${r}-${g}-${b}`;
    }
    return fabricId_;
  }
}
